import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './Partners.css';
import './TableStyles.css';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    observations: '',
    phones: ['']
  });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');

  // Fetch partners
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get('/partners');
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };
    fetchPartners();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = value;
    setFormData({
      ...formData,
      phones: newPhones
    });
  };

  const addPhoneField = () => {
    if (formData.phones.length < 5) {
      setFormData({
        ...formData,
        phones: [...formData.phones, '']
      });
    }
  };

  const removePhoneField = (index) => {
    if (formData.phones.length > 1) {
      const newPhones = formData.phones.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        phones: newPhones
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty phones
      const dataToSend = {
        ...formData,
        phones: formData.phones.filter(p => p.trim() !== '')
      };

      if (editingId) {
        // Update existing partner
        const response = await api.put(`/partners/${editingId}`, dataToSend);
        setPartners(partners.map(partner =>
          partner._id === editingId ? response.data : partner
        ));
        setMessage('Parceiro atualizado com sucesso!');
      } else {
        // Create new partner
        const response = await api.post('/partners', dataToSend);
        setPartners([...partners, response.data]);
        setMessage('Parceiro criado com sucesso!');
      }
      // Reset form
      setFormData({
        name: '',
        cnpj: '',
        observations: '',
        phones: ['']
      });
      setEditingId(null);
    } catch (error) {
      setMessage('Erro: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (partner) => {
    // Handle migration from old phone field to new phones array
    let phones = partner.phones || [];
    if (phones.length === 0 && partner.phone) {
      phones = [partner.phone];
    }
    if (phones.length === 0) {
      phones = [''];
    }
    
    setFormData({
      name: partner.name,
      cnpj: partner.cnpj,
      observations: partner.observations || '',
      phones: phones
    });
    setEditingId(partner._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este parceiro?')) {
      try {
        await api.delete(`/partners/${id}`);
        setPartners(partners.filter(partner => partner._id !== id));
        setMessage('Parceiro excluído com sucesso!');
      } catch (error) {
        setMessage('Erro ao excluir parceiro: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: '',
      cnpj: '',
      observations: '',
      phones: ['']
    });
    setEditingId(null);
  };

  // Format phones for display
  const formatPhones = (partner) => {
    const phones = partner.phones || [];
    if (phones.length > 0) {
      return phones.join(', ');
    }
    // Fallback for old phone field
    return partner.phone || '-';
  };

  return (
    <div className="partners-container">
      <h2>Cadastro de Parceiros</h2>
      {message && (
        <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
      <form onSubmit={handleSubmit} className="partner-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Nome/Razão Social *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="cnpj">CNPJ *</label>
            <input
              type="text"
              id="cnpj"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group phones-group">
            <label>Telefones (até 5)</label>
            {formData.phones.map((phone, index) => (
              <div key={index} className="phone-input-row">
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => handlePhoneChange(index, e.target.value)}
                  placeholder={`Telefone ${index + 1} - Ex: 11999999999`}
                />
                {formData.phones.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove-phone"
                    onClick={() => removePhoneField(index)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {formData.phones.length < 5 && (
              <button
                type="button"
                className="btn-add-phone"
                onClick={addPhoneField}
              >
                + Adicionar Telefone
              </button>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="observations">Observações</label>
            <textarea
              id="observations"
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-save">
            {editingId ? 'Atualizar Parceiro' : 'Adicionar Parceiro'}
          </button>
          {editingId && (
            <button type="button" className="btn-cancel" onClick={handleCancelEdit}>
              Cancelar
            </button>
          )}
        </div>
      </form>
      <div className="partners-list">
        <h3>Parceiros Cadastrados</h3>
        {partners.length > 0 ? (
          <div className="global-table-container">
            <table className="global-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Telefones</th>
                  <th>Observações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {partners.map(partner => (
                  <tr key={partner._id}>
                    <td>{partner.name}</td>
                    <td>{partner.cnpj}</td>
                    <td>{formatPhones(partner)}</td>
                    <td>{partner.observations || '-'}</td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(partner)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(partner._id)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum parceiro cadastrado.</p>
        )}
      </div>
    </div>
  );
};

export default Partners;
